import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { toast } from "sonner";
import {
  Loader2, Plus, FileText, CheckCircle2, MapPin, Send, Link2,
} from "lucide-react";
import { Wizard, WField, WNote } from "@/components/fs/Wizard";
import { ScopeEditor } from "@/components/fs/ScopeEditor";
import { V } from "@/lib/vocab";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");

/* Wizard 1 — start a project. Same four rings and the same endpoints as before, asked one
   question at a time so nobody has to read a wall of form to post a job. */
export default function StartProjectWizard({ tenantId, vendors, onDone, onClose }) {
  const [f, setF] = useState({
    title: "", description: "", scope: "", category: "", mode: "direct", vendor_id: "", invited: [],
    reach: "match", budget: "", bids_due: "", soleReason: "", openBoard: false,
    aud: { trades: [], area_only: true, insured_only: false, min_rating: 0 },
  });
  const [busy, setBusy] = useState(false);
  const [scopeFiles, setScopeFiles] = useState([]);
  // iter372 — the scope always goes to the bidders; the CC list is a switch.
  const [scopeShare, setScopeShare] = useState({ vendor: true, cc: true });
  const [preview, setPreview] = useState(null);
  const approved = (vendors || []).filter((v) => v.network_status === "approved");
  const audience = { ...f.aud, trades: f.aud.trades.length ? f.aud.trades : (f.category ? [f.category] : []) };
  const today = new Date().toISOString().slice(0, 10);
  const setAud = (patch) => setF((p) => ({ ...p, aud: { ...p.aud, ...patch } }));

  useEffect(() => {
    if (f.mode !== "rfp" || f.reach !== "match") { setPreview(null); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/tenants/${tenantId}/field-services/rfp-audience`, audience);
        if (alive) setPreview(data);
      } catch { if (alive) setPreview(null); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, f.mode, f.reach, f.category, JSON.stringify(f.aud)]);

  const create = async () => {
    setBusy(true);
    try {
      const rings = {
        specific: f.mode === "direct" ? { vendor_id: f.vendor_id, reason: f.soleReason } : null,
        chosen: f.mode === "rfp" && f.reach === "pick" ? f.invited : [],
        network: f.mode === "rfp" && f.reach !== "pick" ? audience : false,
        open_market: f.openBoard,
        share_link: f.mode === "link",
      };
      const { data } = await api.post(`/tenants/${tenantId}/field-services/projects`, {
        title: f.title, description: f.description, scope: f.scope, scope_share: scopeShare,
        category: f.category || null, mode: f.mode === "link" ? "rfp" : f.mode, rings,
        budget: f.budget || null, bids_due: f.bids_due || null,
      });
      for (const file of scopeFiles) {
        const fd = new FormData(); fd.append("file", file);
        try { await api.post(`/tenants/${tenantId}/field-services/projects/${data.id}/scope-file`, fd); }
        catch (err) { toast.error(`${file.name}: ${errMsg(err, "couldn’t be attached")}`); }
      }
      const n = (data?.notified_vendor_ids || []).length;
      if (data.share_url) {
        await copyText(data.share_url, "Link copied — paste it to any company you want a price from");
      } else {
        toast.success(data.public_url ? `Listed on the open board${n ? ` and sent to ${n}` : ""}`
          : n ? `Sent to ${n} compan${n === 1 ? "y" : "ies"}` : "Project created");
      }
      onDone?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, "Could not create")); }
    setBusy(false);
  };

  const steps = [
    {
      key: "what", title: "What & where",
      hint: `The title is what everyone sees first — a vendor, your ${V.grp()}, and you in six months.`,
      blocked: !f.title.trim() ? "Give it a title. It's also what the AI drafts the scope from." : null,
      render: () => (
        <>
          <WField label="Project title">
            <input data-testid="np-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
              placeholder="Repaint the clubhouse" autoFocus
              className="w-full rounded-xl border border-sand-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          </WField>
          <WField label="One line for your own list (optional)">
            <textarea data-testid="np-desc" value={f.description} rows={2}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              placeholder="Clubhouse deck, spring"
              className="w-full rounded-xl border border-sand-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          </WField>
          <WField label="Trade (optional)" hint="Sets who hears about it by default, and which budget line it lands on.">
            <select data-testid="np-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
              className="w-full rounded-xl border border-sand-300 bg-white px-3 py-3 text-sm outline-none">
              <option value="">— pick one —</option>
              {Object.entries(CAT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </WField>
        </>
      ),
    },
    {
      key: "scope", title: "Scope of work",
      hint: "This is the part vendors actually price. Write it, paste it, let AI draft it from your title, or hand us the document you already have.",
      render: () => (
        <div className="space-y-3">
          <ScopeEditor tenantId={tenantId} kind="project" value={f.scope} onChange={(s) => setF((p) => ({ ...p, scope: s }))}
            draftCtx={{ title: f.title, category: f.category, budget: f.budget, bids_due: f.bids_due, notes: f.description }}
            pending={scopeFiles} onPending={setScopeFiles} share={scopeShare} onShare={setScopeShare}
            testid="np-scope" textareaTestId="np-scope" />
          <p className="text-xs text-ink/40">
            Every invited vendor sees this scope and can download the documents from their own bid page.
            You can keep editing it until bids close — each save is a new version, and any bid already in resets so nobody prices the wrong wording.
          </p>
        </div>
      ),
    },
    {
      key: "budget", title: "Budget",
      hint: "Your own number, for your own sanity check. Vendors never see it — it only filters what you're shown against what you expected.",
      render: () => (
        <WField label="Budget (optional)">
          <input data-testid="np-budget" value={f.budget} inputMode="decimal" placeholder="e.g. 12000"
            onChange={(e) => setF({ ...f, budget: e.target.value })}
            className="w-full rounded-xl border border-sand-300 px-4 py-3 text-sm outline-none" />
        </WField>
      ),
    },
    {
      key: "who", title: "Who bids",
      hint: "Five rings, and they combine: one company you already trust · companies you tick · the whole network by trade and area · the open market · or nobody yet, and you send the link yourself.",
      blocked: f.mode === "direct"
        ? (!f.vendor_id ? "Pick the company."
          : f.soleReason.trim().length < 5 ? `Going to one company only needs a reason on the record — ${V.aGrp()} will ask.` : null)
        : f.mode === "link" ? null
          : f.reach === "pick" && !f.invited.length ? "Tick at least one company."
            : f.reach === "match" && !audience.trades.length ? "Pick at least one trade to reach." : null,
      render: () => (
        <>
          <div className="grid gap-2 sm:grid-cols-3">
            {[["direct", "One company"], ["rfp", "Open it for bids"], ["link", "Just give me a link"]].map(([k, l]) => (
              <button key={k} data-testid={`np-mode-${k}`} onClick={() => setF({ ...f, mode: k })}
                className={`rounded-xl px-4 py-3 text-sm font-bold transition-colors ${f.mode === k ? "bg-ink text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>{l}</button>
            ))}
          </div>

          {f.mode === "link" ? (
            <WNote testid="np-link-note">
              Nobody is invited and nothing is published. You get one link to text or email — whoever
              holds it reads the brief and sends you a price, no account and no invitation first. Your
              {V.lc("org")}'s name stays hidden until they tell us who they are. You can still open it up
              to the network later.
            </WNote>
          ) : f.mode === "direct" ? (
            <div className="space-y-3">
              <WField label="Which company">
                <select data-testid="np-vendor" value={f.vendor_id} onChange={(e) => setF({ ...f, vendor_id: e.target.value })}
                  className="w-full rounded-xl border border-sand-300 bg-white px-3 py-3 text-sm outline-none">
                  <option value="">Pick an approved company…</option>
                  {approved.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </WField>
              <WField label="Why only this company? (sole source)"
                hint={`Kept on the record. ${V.AGrp()} asked “why didn't we get three bids?” long after everyone forgot the answer — this is the answer.`}>
                <textarea data-testid="np-sole-reason" rows={3} value={f.soleReason}
                  onChange={(e) => setF({ ...f, soleReason: e.target.value })}
                  placeholder="They installed the system and hold the warranty · emergency, nobody else available"
                  className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              </WField>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid gap-1.5">
                {[["match", "Match by trade & area", "The right companies across the network get a text with your brief."],
                  ["pick", "Choose specific companies", "Only the ones you tick."],
                  ["everyone", "Anyone in the network", "Every company in your area for this trade."]].map(([k, l, sub]) => (
                  <button key={k} data-testid={`np-reach-${k}`} onClick={() => setF({ ...f, reach: k })}
                    className={`rounded-lg px-3 py-2.5 text-left transition-colors ${f.reach === k ? "bg-ink text-white" : "bg-sand-50 text-ink/70 hover:bg-sand-100"}`}>
                    <div className="text-sm font-bold">{l}</div>
                    <div className={`text-xs ${f.reach === k ? "text-white/65" : "text-ink/45"}`}>{sub}</div>
                  </button>
                ))}
              </div>

              {f.reach === "match" && (
                <div className="space-y-2.5 rounded-xl border border-sand-200 bg-white p-3" data-testid="np-audience">
                  <div>
                    <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">Trades to reach</div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(CAT_LABEL).map(([k, l]) => {
                        const on = audience.trades.includes(k);
                        return (
                          <button key={k} data-testid={`np-aud-trade-${k}`}
                            onClick={() => setAud({ trades: on ? audience.trades.filter((x) => x !== k) : [...audience.trades, k] })}
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "bg-redline-500 text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>{l}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-ink/60">
                    <label className={`inline-flex items-center gap-2 ${preview?.meta?.market_set === false ? "opacity-50" : "cursor-pointer"}`}>
                      <input type="checkbox" data-testid="np-aud-area" checked={f.aud.area_only}
                        disabled={preview?.meta?.market_set === false}
                        onChange={(e) => setAud({ area_only: e.target.checked })} className="accent-redline-500" />
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Serves {preview?.meta?.market || "our area"}</span>
                    </label>
                    <label className="inline-flex cursor-pointer items-center gap-2">
                      <input type="checkbox" data-testid="np-aud-insured" checked={f.aud.insured_only}
                        onChange={(e) => setAud({ insured_only: e.target.checked })} className="accent-redline-500" />
                      <span>Insurance current</span>
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <span>Rating</span>
                      <select data-testid="np-aud-rating" value={f.aud.min_rating}
                        onChange={(e) => setAud({ min_rating: Number(e.target.value) })}
                        className="rounded-lg border border-sand-300 bg-white px-2 py-1 text-xs outline-none">
                        <option value={0}>Any</option>
                        <option value={3}>3★ and up</option>
                        <option value={4}>4★ and up</option>
                      </select>
                    </label>
                  </div>
                  <p className="text-xs leading-relaxed text-ink/40">
                    Insurance is a <b>bid</b> gate, not a “may we tell you” gate — leaving it off is how the
                    pool of companies who ever hear about your work keeps growing.
                  </p>
                  {preview?.meta?.market_set === false && (
                    <div data-testid="np-aud-no-market" className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
                      This {V.lc("org")} has no city on file, so we can't filter by area — set it under
                      Field Services → Settings. Showing everyone for now.
                    </div>
                  )}
                  <div data-testid="np-aud-preview" className={`rounded-lg px-3 py-2.5 text-xs font-semibold ${preview?.count ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
                    {!preview ? "Working out who this reaches…" : preview.count === 0
                      ? "No company matches yet — widen the area or drop a filter."
                      : `${preview.count} compan${preview.count === 1 ? "y" : "ies"} will get this: ${preview.vendors.slice(0, 3).map((v) => v.name).join(", ")}${preview.count > 3 ? ` +${preview.count - 3} more` : ""}`}
                  </div>
                </div>
              )}

              {f.reach === "pick" && (
                <div className="space-y-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-ink/40">Invite specific companies</div>
                  {approved.length === 0 && <div className="text-xs text-ink/45">No approved companies here yet — try matching by trade instead.</div>}
                  {approved.map((v) => {
                    const on = f.invited.includes(v.id);
                    return (
                      <button key={v.id} data-testid={`np-invite-${v.id}`}
                        onClick={() => setF({ ...f, invited: on ? f.invited.filter((x) => x !== v.id) : [...f.invited, v.id] })}
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold ${on ? "bg-redline-500 text-white" : "bg-sand-50 text-ink/60"}`}>
                        <CheckCircle2 className="h-4 w-4" /> {v.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      key: "open", title: "The open market",
      hint: "Ring four. A company nobody in your network has heard of can find this and register in thirty seconds.",
      blocked: f.mode === "rfp" && f.reach === "match" && preview && preview.count === 0 && !f.openBoard
        ? "As it stands this reaches nobody — no company matches your filters, and the open board is off. Tick the open board below, or go back and widen the trades or the area."
        : null,
      render: () => (
        <label className="flex items-start gap-2.5 rounded-xl border border-sand-200 bg-white px-4 py-3.5"
          data-testid="np-open-board-row">
          <input type="checkbox" data-testid="np-open-board" checked={f.openBoard} className="mt-0.5 accent-redline-500"
            onChange={(e) => setF({ ...f, openBoard: e.target.checked })} />
          <span>
            <span className="block text-sm font-bold text-ink">Also list it on the open board</span>
            <span className="block text-xs leading-relaxed text-ink/50">
              Anyone can find it at <b>/projects</b> and register to bid. Your {V.lc("org")}'s name stays
              hidden until they tell us who they are.
            </span>
          </span>
        </label>
      ),
    },
    f.mode !== "direct" && {
      key: "deadline", title: "Deadline",
      hint: "We nudge everyone who opened it 48 hours out, and tell you the moment the deadline passes with nothing in it.",
      render: () => (
        <WField label="Bids due (optional)" hint="After this date the bid form closes for vendors.">
          <input type="date" data-testid="np-bids-due" value={f.bids_due} min={today}
            onChange={(e) => setF({ ...f, bids_due: e.target.value })}
            className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
        </WField>
      ),
    },
    {
      key: "review", title: "Review & post",
      render: () => {
        const vendor = approved.find((v) => v.id === f.vendor_id);
        const reachLine = f.mode === "direct" ? `${vendor?.name || "one company"} only`
          : f.mode === "link" ? "nobody yet — you send the link"
            : f.reach === "pick" ? `${f.invited.length} company/companies you picked`
              : preview?.count ? `${preview.count} matching compan${preview.count === 1 ? "y" : "ies"}`
                : "the network, by trade & area";
        return (
          <>
            <div className="rounded-xl border border-sand-200 bg-white p-4" data-testid="np-review">
              <div className="font-heading text-base font-bold text-ink">{f.title || "Untitled project"}</div>
              {f.description && <div className="mt-0.5 text-xs text-ink/55">{f.description}</div>}
              <div className="mt-2.5 space-y-1 text-xs text-ink/60">
                <div>{CAT_LABEL[f.category] || "No trade set"} · budget {money(f.budget)}</div>
                <div>Goes to: {reachLine}{f.openBoard ? " + the open board" : ""}</div>
                <div>{f.scope ? `${f.scope.trim().split(/\s+/).length} words of scope` : "No scope written yet"}
                  {scopeFiles.length ? ` · ${scopeFiles.length} document(s) attached` : ""}</div>
                {f.mode !== "direct" && <div>Bids due {f.bids_due || "— no deadline set"}</div>}
              </div>
            </div>
            {!f.scope.trim() && (
              <WNote tone="amber" testid="np-no-scope">
                No scope of work. You can post it anyway and add it before bids land, but a vendor pricing
                a job from a title alone will price it high.
              </WNote>
            )}
            <WNote testid="np-review-note">
              {f.mode === "link"
                ? "Nothing is sent when you press the button — you get the link, and you decide who sees it. Whoever you send it to can read the brief and send you a price without an account."
                : "Vendors get a text and an email with the brief and their own bid page. Nobody sees anyone else's price, and nobody sees your budget."}
            </WNote>
          </>
        );
      },
    },
  ];

  return (
    <Wizard testid="wiz-project" steps={steps} busy={busy} onFinish={create}
      finishLabel={f.mode === "direct" ? "Send it to them"
        : f.mode === "link" ? "Create it & copy the link" : "Post it & request bids"}
      finishIcon={f.mode === "direct" ? Send : f.mode === "link" ? Link2 : Plus} />
  );
}
